import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useQuery } from '@apollo/client';

import {
  DrawerContext,
  ModalContext,
  ModalModel,
} from '@notino/react-styleguide';
import {
  GetProductImagesQuery,
  GetProductImagesQueryVariables,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { trackDrawerClose } from '@components/BottomSheet/tracking';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { isDesktop } from '@utils/helpers';

import { PopupBox } from '../../../../../components/PopupBox';
import getProductImagesQuery from '../../../../../queries/getProductsImages.graphql';
import { useProductDetailContext } from '../../../ProductDetailContext';
import { trackElementClick } from '../hooks/useTrackExtras';

import { AccessoriesContent } from './AccessoriesContent';
import { accessoriesMessages } from './messages';
import { Title } from './styled';

const Accessories = () => {
  const { product } = useProductDetailContext();
  const collection = product?.collection;
  const name = product?.brand?.name;
  const accessories = product?.features?.accessories;

  const { getTimeFromInit } = useTrackingContext();

  const { data, loading } = useQuery<
    GetProductImagesQuery,
    GetProductImagesQueryVariables
  >(getProductImagesQuery, {
    variables: {
      ids: accessories?.products
        ? [String(accessories.products[0].catalogId)]
        : [],
      idType: ProductIdType.Catalog,
    },
    ssr: false,
    skip: !accessories?.products,
  });

  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const { toggleDrawer } = useDrawer();

  if (!accessories?.products) {
    return null;
  }

  const accessoryCount = accessories.products.length;

  const openModalOrDrawer = () => {
    trackElementClick({
      timing: getTimeFromInit(),
      action: 'accessories_open',
      type: 'accessories',
      name: 'accessories_open',
      interaction: 'click',
    });
    const productName = `${name ?? ''} ${collection ?? ''}`;
    const header = (
      <Title>
        <FormattedMessage
          {...accessoriesMessages.accessoriesContentTitle}
          values={{ productName, count: accessoryCount }}
        />
      </Title>
    );

    if (isDesktop()) {
      toggleModal(
        // DrawerContext.Provider is used to be able to render wishlist list selection drawer from inside modal
        <DrawerContext.Provider>
          <AccessoriesContent accessories={accessories} />
        </DrawerContext.Provider>,
        {
          header,
          size: ModalModel.ModalSizes.large,
          withFocusTrap: true,
          onClose: () => {
            trackElementClick({
              timing: getTimeFromInit(),
              name: 'accessories_close',
              type: 'accessories',
              action: 'accessories_close',
            });
            hideModal();
          },
        }
      );
      return;
    }

    toggleDrawer(<AccessoriesContent accessories={accessories} />, {
      header,
      onDismiss: (closeType) =>
        trackDrawerClose({
          closeType,
          timing: getTimeFromInit(),
          name: 'accessories_close',
          type: 'accessories',
          action: 'accessories_close',
        }),
      ...(accessoryCount >= 3 && { ignoreBodySwipe: true }),
    });
  };

  const imageUrl =
    (!loading && data?.vpProductByIds?.[0]?.imageUrl) || undefined;

  return (
    <PopupBox
      dataTestId="accessories-wrapper"
      cdnImageUrl={imageUrl}
      onClick={openModalOrDrawer}
    >
      <FormattedMessage
        {...accessoriesMessages.accessoriesPopupBoxText}
        values={{ count: accessoryCount }}
      />
    </PopupBox>
  );
};

export default Accessories;
